import { useTranslation } from 'react-i18next';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import TooltipHandler from '@/components/ui/custom/TooltipHandler';
import LessonForm from '@/components/forms/LessonForm';
import useAuth from '@/data/hook/useAuth';
import useCourseEditing from '@/data/hook/useCourseEditing';
import CoursePath from '@/models/CoursePath';
import Lesson from '@/models/Lesson';
import { TrashIcon } from '@heroicons/react/24/outline';
import { UserTypeEnum } from '@/models/User';

interface EditLessonProps {
  lesson: Lesson;
  course: CoursePath;
  blockId?: number;
  updateLessons: () => Promise<void>;
  deleteLesson(lesson: Lesson): void;
}

export default function LessonDetails({
  lesson,
  course,
  blockId,
  updateLessons,
  deleteLesson,
}: EditLessonProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { allowBigChanges } = useCourseEditing();

  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  return (
    <div className="flex flex-col gap-4">
      <LessonForm
        slugCourseName={course.slug}
        lesson={lesson}
        blockId={blockId}
        updateLessons={updateLessons}
        userType={user?.userType ?? UserTypeEnum.SUPER_ADMIN}
      />

      <hr className="h-px my-4 border-secondary-content" />

      <TooltipHandler
        renderTooltip={!allowBigChanges}
        tooltipMessage={editRulesT('cantModify')}
      >
        <MainButton
          className="flex self-end"
          disabled={!allowBigChanges}
          color="warning"
          icon={<TrashIcon />}
          onClick={() => deleteLesson(lesson)}
          text={t('adminPage.lesson.lessonDetails.deleteLesson')}
        />
      </TooltipHandler>
    </div>
  );
}
